import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { getApiErrorMessage } from '../../constants/messages';
import { VERSION_CONDITIONS, URL_CONDITIONS, URL_CONFIDENTIALITE } from '../../constants/legal';

/**
 * Recueille l'acceptation des conditions pour un compte DEJA CREE.
 *
 * POURQUOI CE BANDEAU EXISTE. L'acceptation n'est exigee qu'aux inscriptions et aux
 * acceptations d'invitation : tout compte anterieur au dispositif porte `null`, ce qui
 * dit la verite mais ne prouve rien. C'est le seul chemin par lequel ces comptes-la
 * peuvent donner leur acceptation.
 *
 * ET IL SERT AUSSI AUX VERSIONS SUIVANTES. La condition d'affichage n'est pas « jamais
 * accepte » mais « la version acceptee differe de la version courante » : le jour d'une
 * version 1.1, le meme bandeau reapparait pour tout le monde. Les conditions promettent
 * une nouvelle acceptation explicite a chaque nouvelle version, et c'est ici qu'elle se
 * recueille -- sans quoi cette promesse serait une clause sans mecanisme.
 *
 * IL NE BLOQUE PAS, et c'est un choix assume : la console reste utilisable. Il ne
 * recueille donc que l'acceptation de ceux qui cliquent, et c'est a savoir -- un
 * bandeau ignore ne prouve rien de plus qu'une absence de bandeau.
 */
const BandeauConditions: React.FC = () => {
  const { t } = (useTranslation as any)('common');
  const { user, refreshUser } = useAuth();
  const [envoi, setEnvoi] = useState(false);

  // `null` n'est PAS une acceptation : un compte qui n'a jamais rien accepte entre ici
  // au meme titre qu'un compte reste sur une version anterieure.
  const aAccepte = user?.conditions_version_acceptee === VERSION_CONDITIONS;
  if (!user || aAccepte) return null;

  const accepter = async () => {
    setEnvoi(true);
    try {
      // La reponse porte la fiche a jour : on la passe au contexte plutot que de
      // declencher un second GET pour relire ce qu'on tient deja.
      const majour = await apiService.accepterConditions(VERSION_CONDITIONS);
      await refreshUser(majour);
      toast.success(t('conditions.merci') as string);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('conditions.echec') as string));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="border-b border-accent-500/30 bg-accent-500/10 px-6 py-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-text-primary">
          {t('conditions.texte')}{' '}
          <a href={URL_CONDITIONS} target="_blank" rel="noopener noreferrer"
             className="font-medium text-accent-400 underline hover:text-accent-300">
            {t('conditions.lienConditions')}
          </a>{' '}
          <span className="text-text-tertiary">
            ({t('conditions.version', { version: VERSION_CONDITIONS })})
          </span>{' '}
          {t('conditions.et')}{' '}
          <a href={URL_CONFIDENTIALITE} target="_blank" rel="noopener noreferrer"
             className="font-medium text-accent-400 underline hover:text-accent-300">
            {t('conditions.lienConfidentialite')}
          </a>
          .
        </p>
        <button
          type="button"
          onClick={accepter}
          disabled={envoi}
          className="shrink-0 rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50"
        >
          {envoi ? t('conditions.enCours') : t('conditions.bouton')}
        </button>
      </div>
    </div>
  );
};

export default BandeauConditions;
